import { easepick } from '@easepick/core';
import { IEventDetail } from '@easepick/core/dist/types';
import { DateTime } from '@easepick/datetime';
import { LockPlugin } from '@easepick/lock-plugin';
import { RangePlugin } from '@easepick/range-plugin';
import { Component } from '@opten/compiler/metadata/component';
import { addClass } from '@opten/dom/add-class';
import { toggleClass } from '@opten/dom/toggle-class';
import { debounce } from '@opten/reactive/debounce';

declare const Swiper: any;

@Component({ className: 'js-date-range' })
export class DateRangeComponent {
	private form: HTMLFormElement;
	private swiper: any;
	private datePicker: easepick.Core;
	private today = new DateTime();
	private bookedDates: (DateTime | DateTime[])[] = [];
	private transportDates: (DateTime | DateTime[])[] = [];
	private activeTransportDates: (DateTime | DateTime[])[] = [];
	private daysToUse = 0;
	private pickupExtraDays = 0;
	private pickupType: string;
	private returnExtraDays = 0;
	private returnType: string;

	constructor(private element: HTMLElement) {
		const that = this;
		const slider = this.element.querySelector(
			'.js-date-range-slider'
		) as HTMLElement;
		const picker = this.element.querySelector(
			'.js-date-range-picker'
		) as HTMLElement;
		this.form = this.element.querySelector(
			'.js-date-range-form'
		) as HTMLFormElement;
		const swiperPagination = this.element.querySelector(
			'.js-date-range-slider-pagination'
		) as HTMLElement;
		const swiperPrev = this.element.querySelector(
			'.js-date-range-slider-prev'
		) as HTMLElement;
		const swiperNext = this.element.querySelector(
			'.js-date-range-slider-next'
		) as HTMLElement;
		const nextSome = this.element.querySelector(
			'.js-date-range-slider-next-some'
		) as HTMLElement;
		const nextLast = this.element.querySelector(
			'.js-date-range-slider-next-last'
		) as HTMLElement;

		const daysToUseField = this.element.querySelector(
			'.js-date-range-days-to-use'
		) as HTMLInputElement;
		const pickupTypes = Array.from(
			this.element.querySelectorAll('.js-date-range-pickup')
		) as HTMLInputElement[];
		const returnTypes = Array.from(
			this.element.querySelectorAll('.js-date-range-return')
		) as HTMLInputElement[];

		const vCal = this.element.querySelector(
			'.js-date-range-vcal'
		) as HTMLElement;

		const daysToUseText =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-days-to-use-text')
			) as HTMLElement[]) || [];
		const daysToUseStartDate =
			(Array.from(
				this.element.querySelectorAll(
					'.js-date-range-days-to-use-start-value'
				)
			) as HTMLInputElement[]) || [];
		const daysToUseEndDate =
			(Array.from(
				this.element.querySelectorAll(
					'.js-date-range-days-to-use-end-value'
				)
			) as HTMLInputElement[]) || [];
		const pickupText =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-pickup-text')
			) as HTMLElement[]) || [];
		const pickupDate =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-pickup-date')
			) as HTMLElement[]) || [];
		const returnText =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-return-text')
			) as HTMLElement[]) || [];
		const returnDateDisplay =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-return-date')
			) as HTMLElement[]) || [];
		const rent =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-rent')
			) as HTMLElement[]) || [];
		const shipping =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-shipping')
			) as HTMLElement[]) || [];
		const vat =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-vat')
			) as HTMLElement[]) || [];
		const total =
			(Array.from(
				this.element.querySelectorAll('.js-date-range-total')
			) as HTMLElement[]) || [];
		var rentFee = parseFloat(rent[0].getAttribute('data-value'));
		var shippingFee = parseFloat(shipping[0].getAttribute('data-value'));
		var vatPercent = parseFloat(vat[0].getAttribute('data-value')) / 100;

		const bookedDateStrings =
			JSON.parse(picker.getAttribute('data-reservations')) || [];
		this.bookedDates =
			bookedDateStrings.length > 0
				? bookedDateStrings.map((d) => {
						if (d instanceof Array) {
							const start = new DateTime(d[0], 'YYYY-MM-DD');
							const end = new DateTime(d[1], 'YYYY-MM-DD');

							return [start, end];
						}

						return new DateTime(d, 'YYYY-MM-DD');
				  })
				: [];

		this.datePicker = new easepick.create({
			element: picker,
			css: ['/css/daterange.css'],
			plugins: [RangePlugin, LockPlugin],
			lang: document.documentElement.lang,
			inline: true,
			readonly: false,
			documentClick: false,
			calendars: this.getAmountOfMonthCalendars(),
			grid: this.getAmountOfMonthCalendarsGrid(),
			RangePlugin: {
				tooltip: false,
				delimiter: '|',
				locale: {
					one: 'Tag',
					other: 'Tage',
				},
			},
			setup(picker) {
				picker.on('view', (e) => {
					const { view, date, target }: IEventDetail = e.detail;
					if (view === 'CalendarDay') {
						const isWeekend = that.isWeekend(date);
						const isBooked = date.inArray(that.bookedDates, '[]');
						if (isWeekend || isBooked) {
							target.classList.add('not-available');
							if (isBooked) {
								target.classList.add('booked');
							}
						}
						if (
							that.activeTransportDates &&
							that.activeTransportDates.length == 2
						) {
							if (date.inArray(that.activeTransportDates, '[]')) {
								target.classList.add('transport');
							}
						}
					}
				});

				picker.on('select', (e) => {
					that.activeTransportDates = [];
					if (e.detail && e.detail.start && e.detail.end) {
						const start: DateTime = e.detail.start.clone();
						const end: DateTime = e.detail.end.clone();
						const startToUse = that.nextDaysSkipWeekend(
							start,
							that.pickupExtraDays
						);
						const endToUse = that.prevDaysSkipWeekend(
							end,
							that.returnExtraDays
						);
						const startRange =
							that.pickupExtraDays > 0
								? that.prevDaySkipWeekend(startToUse)
								: start;
						const endRange =
							that.returnExtraDays > 0
								? that.nextDaySkipWeekend(endToUse)
								: end;
						pickupDate.forEach(
							(o) => (o.innerHTML = start.format('DD.MM.YYYY'))
						);
						daysToUseText.forEach(
							(o) =>
								(o.innerHTML = `${daysToUseField.value} ${
									that.daysToUse == 1
										? that.datePicker.options.RangePlugin
												.locale.one
										: that.datePicker.options.RangePlugin
												.locale.other
								} (${
									that.daysToUse == 1
										? startToUse.format('DD.MM.YYYY')
										: `${startToUse.format(
												'DD.MM'
										  )} - ${endToUse.format('DD.MM.YYYY')}`
								})`)
						);
						returnDateDisplay.forEach(
							(o) => (o.innerHTML = endRange.format('DD.MM.YYYY'))
						);
						daysToUseStartDate.forEach(
							(o) => (o.value = startToUse.format('YYYY-MM-DD'))
						);
						daysToUseEndDate.forEach(
							(o) => (o.value = endToUse.format('YYYY-MM-DD'))
						);
						that.activeTransportDates = [
							[start, startRange],
							[endRange, end],
						];

						vCal.setAttribute(
							'href',
							`${vCal.getAttribute('data-url')}&culture=${
								document.documentElement.lang
							}&pickupDate=${start.format(
								'YYYY-MM-DD'
							)}&daysToUseStartDate=${startToUse.format(
								'YYYY-MM-DD'
							)}&daysToUse=${
								that.daysToUse
							}&daysToUseEndDate=${endToUse.format(
								'YYYY-MM-DD'
							)}&returnDate=${end.format(
								'YYYY-MM-DD'
							)}&pickupType=${that.pickupType}&returnType=${
								that.returnType
							}`
						);
					}

					let subtotal = rentFee;
					if (shipping[0].clientHeight > 0) {
						subtotal += shippingFee;
					}
					const vatFee = that.roundUpToFive(subtotal * vatPercent);
					vat.forEach((o) => {
						o.innerHTML = vatFee.toFixed(2);
					});
					total.forEach((o) => {
						o.innerHTML = that.roundUpToFive(subtotal + vatFee).toFixed(2);
					});
				});
			},
			LockPlugin: {
				minDate: this.today,
				selectForward: true,
				selectBackward: false,
				inseparable: true,
				filter(date: DateTime, picked: DateTime[]) {
					if (that.pickupExtraDays > 0) {
						const minStartDate = that.nextDaysSkipWeekend(
							that.today,
							that.pickupExtraDays + 1
						);
						if (date.isBefore(minStartDate)) {
							return true;
						}
					}
					if (picked && picked.length > 0) {
						if (picked.length == 1) {
							const startDate = that.prevDaysSkipWeekend(
								picked[0].clone(),
								that.pickupExtraDays
							);
							let endDate = that.nextDaysSkipWeekend(
								picked[0].clone(),
								that.daysToUse - 1 + that.returnExtraDays
							);
							that.datePicker.datePicked = [startDate, endDate];
							that.datePicker.setDateRange(startDate, endDate);
							const startDateOfCalendarView = new DateTime(
								that.datePicker.calendars[0].format(
									'YYYY-MM-01'
								)
							);
							const endDateOfCalendarView =
								startDateOfCalendarView
									.clone()
									.add(
										that.datePicker.options.calendars,
										'month'
									)
									.subtract(1, 'day');
							if (
								startDate.isBefore(startDateOfCalendarView) ||
								endDate.isAfter(endDateOfCalendarView)
							) {
								that.datePicker.gotoDate(startDate);
							}
							that.datePicker.trigger('select', {
								start: startDate,
								end: endDate,
							});
						}
					}
					const isDisabled = date.inArray(that.transportDates, '[]');
					return isDisabled;
				},
			},
		});

		this.swiper = new Swiper(slider, {
			speed: 1000,
			autoHeight: true,
			pagination: {
				el: swiperPagination,
				type: 'progressbar',
			},
			allowTouchMove: false,
			// watchSlidesVisibility: true,
			// watchSlidesProgress: true,
			slidesPerView: 'auto',
			spaceBetween: 30,
			breakpoints: {
				992: {
					autoHeight: false,
				},
			},
			on: {
				init: () => {
					toggleClass(swiperPrev, 'is-disabled', true); //this.swiper.isBeginning
				},
				click: () => {
					if (this.swiper.clickedIndex > this.swiper.activeIndex) {
						let goToSlide = this.swiper.activeIndex;
						for (
							let i = this.swiper.activeIndex;
							i < this.swiper.clickedIndex;
							i++
						) {
							goToSlide = i;
							if (this.isSlideValid(this.swiper.slides[i])) {
								goToSlide = i + 1;
							} else {
								break;
							}
						}
						this.swiper.slideTo(goToSlide);
					}
				},
				slideChangeTransitionStart: () => {
					if (
						this.swiper.slides[
							this.swiper.activeIndex
						].querySelector('.js-date-range-picker')
					) {
						this.datePicker.renderAll();
					}
					toggleClass(
						swiperPrev,
						'is-disabled',
						this.swiper.isBeginning
					);
					toggleClass(nextSome, 'd-none', this.swiper.isEnd);
					toggleClass(nextLast, 'd-none', this.swiper.isEnd == false);
				},
			},
		});

		window.addEventListener(
			'resize',
			debounce(() => {
				this.datePicker.options.calendars =
					this.getAmountOfMonthCalendars();
				this.datePicker.options.grid =
					this.getAmountOfMonthCalendarsGrid();
				this.datePicker.renderAll();
			}, 100)
		);

		this.form.addEventListener('submit', (e) => {
			e.preventDefault();
			if (this.isSlideValid()) {
				if (this.swiper.isEnd && this.form.reportValidity()) {
					addClass(swiperNext, 'is-disabled');
					return true;
				}
			}
			return false;
		});
		swiperPrev.addEventListener('click', (e) => {
			this.swiper.slidePrev();
		});
		swiperNext.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			if (this.isSlideValid()) {
				if (this.swiper.isEnd) {
					if (this.form.reportValidity()) {
						addClass(swiperNext, 'is-disabled');
						this.form.submit();
					}
				} else {
					this.swiper.slideNext();
				}
			}
		});
		daysToUseField.addEventListener('change', () => {
			this.daysToUse = parseInt(daysToUseField.value);
			this.updateTransportDates();
		});
		pickupTypes.forEach((pickupType) => {
			pickupType.addEventListener('change', () => {
				if (pickupType.checked) {
					this.pickupType = pickupType.value;
					this.pickupExtraDays = parseInt(
						pickupType.getAttribute('data-extra-days')
					);
					pickupText.forEach(
						(o) =>
							(o.innerHTML = pickupType.getAttribute('data-text'))
					);
					this.updateTransportDates();
				}
			});
		});
		returnTypes.forEach((returnType) => {
			returnType.addEventListener('change', () => {
				if (returnType.checked) {
					this.returnType = returnType.value;
					this.returnExtraDays = parseInt(
						returnType.getAttribute('data-extra-days')
					);
					returnText.forEach(
						(o) =>
							(o.innerHTML = returnType.getAttribute('data-text'))
					);
					this.updateTransportDates();
				}
			});
		});
	}

	private isSlideValid(slideToCheck = null) {
		slideToCheck =
			slideToCheck || this.swiper.slides[this.swiper.activeIndex];
		const fields = Array.from(
			slideToCheck.querySelectorAll('[name]')
		) as HTMLFormElement[];
		let isValid = true;
		if (fields && fields.length > 0) {
			isValid =
				fields.some((field) => field.reportValidity() == false) ==
				false;
		}
		return isValid;
	}

	private updateTransportDates() {
		this.transportDates = [
			this.today,
			this.nextDaysSkipWeekend(this.today, this.pickupExtraDays - 1),
		];
		this.bookedDates.forEach((date) => {
			let start = date[0];
			let end = date[1];
			if (date instanceof Array) {
				start = date[0];
				end = date[1];
			} else {
				start = date;
				end = date;
			}

			let checkDate = start.clone();
			for (
				let i = 1;
				i <= this.daysToUse - 1 + this.returnExtraDays;
				i++
			) {
				checkDate = this.prevDaySkipWeekend(checkDate);
				if (checkDate.inArray(this.bookedDates, '[]') == false) {
					this.transportDates.push(checkDate);
				}
			}
			checkDate = end.clone();
			for (let i = 1; i <= this.pickupExtraDays; i++) {
				checkDate = this.nextDaySkipWeekend(checkDate);
				if (checkDate.inArray(this.bookedDates, '[]') == false) {
					this.transportDates.push(checkDate);
				}
			}
		});
		this.datePicker.clear();
		this.datePicker.trigger('select', undefined);
		if (this.isSlideValid()) {
			this.swiper.slideNext();
		}
	}

	private isWeekend(date: DateTime) {
		return [6, 0].includes(date.getDay());
	}

	private nextDaySkipWeekend(date: DateTime) {
		let newDate = date.clone();
		do {
			newDate.add(1, 'day');
		} while (this.isWeekend(newDate));
		return newDate;
	}

	private nextDaysSkipWeekend(date: DateTime, days: number) {
		let newDate = date.clone();
		for (let i = 1; i <= days; i++) {
			newDate = this.nextDaySkipWeekend(newDate);
		}
		return newDate;
	}

	private prevDaySkipWeekend(date: DateTime) {
		let newDate = date.clone();
		do {
			newDate.subtract(1, 'day');
		} while (this.isWeekend(newDate));
		return newDate;
	}

	private prevDaysSkipWeekend(date: DateTime, days: number) {
		let newDate = date.clone();
		for (let i = 1; i <= days; i++) {
			newDate = this.prevDaySkipWeekend(newDate);
		}
		return newDate;
	}

	private getAmountOfMonthCalendars() {
		return window.innerWidth > 992 ? 3 : 2;
	}

	private getAmountOfMonthCalendarsGrid() {
		return window.innerWidth > 992 ? 3 : window.innerWidth > 768 ? 2 : 1;
	}

	private roundUpToFive(number: number) {
		const numberStr = number.toFixed(2);
		let newNumber = parseFloat(numberStr);
		switch (numberStr.substring(numberStr.length - 1)) {
			case '1':
				newNumber += 0.04;
				break;
			case '2':
				newNumber += 0.03;
				break;
			case '3':
				newNumber += 0.02;
				break;
			case '4':
				newNumber += 0.01;
				break;
			case '6':
				newNumber += 0.04;
				break;
			case '7':
				newNumber += 0.03;
				break;
			case '8':
				newNumber += 0.02;
				break;
			case '9':
				newNumber += 0.01;
				break;
		}
		return newNumber;
	}
}
